<template>
    <div :class="{ 'has-extra-layer': isExtraLayerVisible }">
        <div
            ref="container"
            class="map is-overlay">
            <div
                v-if="!readOnly"
                class="column column-options">
                <div class="navigation-button">
                    <LayerManager
                        ref="layerManager" v-bind="$props"
                        @shownExtraLayer="isExtraLayerVisible = true"
                        @hiddenExtraLayer="isExtraLayerVisible = false"
                        v-on="$listeners" :map="map" />

                    <button class="button mr-1 is-navigate-small" @click="zoomIn">
                        <img
                            src="../assets/icon-zoom-in.svg"
                            height="20px" width="20px" />
                    </button>
                    <button class="button mr-1 is-navigate-small" @click="zoomOut">
                        <img
                            src="../assets/icon-zoom-out.svg"
                            height="20px" width="20px" />
                    </button>
                    <button
                        class="button mr-1 is-navigate-small"
                        @click="resetBearing">
                        <img
                            src="../assets/icon-gps.svg"
                            height="20px" width="20px" />
                    </button>
                </div>
            </div>
            <PolygonContainer v-if="drawingMode" />
        </div>
    </div>
</template>

<script>
import * as tt from '@tomtom-international/web-sdk-maps/dist/maps.min';

import TTMixin from '../mixins/TTMixin';

import LayerManager, { SharedProps as LMProps } from './LayerManager.vue';
import PolygonContainer from './PolygonContainer.vue';
import { FlagMarker, ArrowMarker } from './markers';

// eslint-disable-next-line no-underscore-dangle
tt.__core.maxParallelRequests = 100;

/**
 * Creates marker element to be attached to the map with given content.
 * @param {String} content - the content (preferably SVG) to be included
 *     in the marker.
 */
function createMarkerElement(content = '') {
    const markerContainer = document.createElement('div');
    markerContainer.setAttribute(
        'class',
        'mapboxgl-marker mapboxgl-marker-anchor-bottom',
    );
    markerContainer.style.backgroundImage = 'none';
    markerContainer.style.width = '30px';
    markerContainer.style.height = '36px';

    markerContainer.innerHTML = content;

    return markerContainer;
}

/**
 * Adjusts the in-world orientation to the map orientation so the arrow
 * points in the right direction.
 * @param {Number} orientation - the in-world orientation in degrees.
 */
function adjustOrientation(orientation) {
    return orientation + 90;
}

/**
 * This component provides integration with TomTom map component. It's
 * existence allows for separation of side effects related to the TomTom
 * map manipulation. This component must not contain any references to
 * Vuex store.
 * Emits `selectedCoordinates` event when user clicks the position on the
 * map. The emitted value is object containing longitude and latitude of
 * clicked point.
 * Emits 'panoramasToggled` event with current panoramas visibility
 * status whenver the panoramas are toggled.
 */
export default {
    mixins: [TTMixin],

    components: {
        LayerManager,
        PolygonContainer,
    },

    props: {
        ...LMProps,

        /**
         * The API key for the maps API. Used to load data.
         * @type {string}
         */
        apiKey: {
            type: String,
        },

        /**
         * List of location data to render as markers.
         * @type {LngLat}
         * @default {null}
         */
        markers: {
            type: Array,
            default: () => [],
        },

        /**
         * Position of the arrow marking user position.
         * @type {Object}
         */
        arrow: {
            type: Object,
            default: null,
        },

        /**
         * Orientation of the arrow marking user position.
         * @type {Number}
         * @default 0
         */
        orientation: {
            type: Number,
            default: 0.0,
        },

        /**
         * Prop which is an array of two points,
         * TL - top left and BR - bottom right,
         * designating viewport to look at selected search marker.
         * Each point is in format `[lng,lat]`.
         * This name is taken from TomTom/Mapbox API.
         * @type {Array}
         */
        bounds: {
            type: Array,
        },

        /**
         * Prop which is an array of two points,
         * TL - top left and BR - bottom right.
         * Each point is in format `[lng,lat]`.
         * When not present (e.g. in panorama mode) the map
         * does not automatically pan & zoom to a given location.
         * Unlike @see bounds, these are not watched, to avoid
         * infinite recursion as map emits news bounds after refitting.
         * @type {Object}
         */
        initialBounds: {
            type: Array,
        },

        /**
         * Read-only map is locked for most of user actions, like panning,
         * zooming, etc.
         * @type {boolean}
         * @default {false}
         */
        readOnly: {
            type: Boolean,
            default: false,
        },

        /**
         * Prop with the central point of the viewed panorama.
         * For normal map view (in Map.vue) it is `undefined`,
         * not setting any viewpoint.
         * @type {Object}
         */
        center: {
            type: Object,
        },

        /**
         * Prop setting zoom at given level for map
         * when panorama is viewed.
         * For normal map view it is `undefined`,
         * not setting any view zoom.
         * @type {number}
         */
        zoomLevel: {
            type: Number,
        },

        /**
         * tt.LngLat coordinates of vertices of the drawn polygon.
         * @type {Object[]}
         */
        drawingLngLats: {
            type: Array,
        },

        /**
         * Flag for polygon drawing mode.
         * @type {boolean}
         */
        drawingMode: {
            type: Boolean,
        },

        /**
         * Flag for whether polygon is fully drawn.
         * @type {boolean}
         */
        drawingFinished: {
            type: Boolean,
        },
    },

    data() {
        return {
            /**
             * Reference to the Marker object representing the user
             * position.
             */
            positionArrow: null,

            /**
             * Reference to the map managed by this component.
             */
            map: null,

            /**
             * Determines if the extra layer is visible. This flag is
             * used to display pointer cursor on the map.
             */
            isExtraLayerVisible: false,
        };
    },

    /**
     * `Activated` is a Vue lifecycle hook,
     *  in this case causes map resize
     *  when view is switched to this component.
     */
    activated() {
        this.map.resize();
    },

    computed: {
        /**
         * Creation & event binding of map markers,
         * which represent search results.
         */
        mapMarkers() {
            return this.markers.map((result) => {
                const { position: { lng, lat } } = result;
                const element = createMarkerElement(FlagMarker);
                const marker = new tt.Marker(element)
                    .setLngLat([lng, lat]);

                /* If map is `readOnly`, clicking on markers does nothing,
                 * otherwise it sends `selectMarker` event. */
                if (!this.readOnly) {
                    element.addEventListener('click', () => {
                        this.$emit('selectMarker', result);
                    });
                }
                return marker;
            });
        },

        /**
         * Returns LngLat in TomTom's format (Mapbox format).
         * If map is centered on some point, this function returns
         * lngLat object. If any of coords does not exist, returns null.
         * This function is created to handle null values. TomTom's map
         * does not accept null values.
         */
        centerLngLat() {
            const { lng, lat } = this.center || {};

            return lng == null || lat == null ? null : new tt.LngLat(lng, lat);
        },
    },

    methods: {
        /**
         *  Map clearing, with deinitialization of
         *  the underlying map component.
         */
        clearMap() {
            if (!this.map) {
                return;
            }

            this.map.remove();
            this.map = null;
        },

        setupMap() {
            /**
             * Creates main map object.
             * @param {string} key Uses maps API key to retrieve data.
             * @param {Object} container map DOM element will be in reference
             * container named `container`, defined in this component's template.
             * @param {string} style Loads predetermined map style.
             * @param {Array} bounds Centers & zooms map to given rectangle in `bounds`.
             *
             * All params with value `!this.readOnly` - They lock map navigation
             * when flag `readOnly` is `true`.
             *
             * @param {number} zoom Sets given zoom level.
             */
            const options = {
                key: this.apiKey,
                container: this.$refs.container,
                style: 'tomtom://vector/1/basic-main',
                bounds: this.initialBounds,
                pitchWithRotate: !this.readOnly,
                scrollZoom: !this.readOnly,
                boxZoom: !this.readOnly,
                dragRotate: !this.readOnly,
                dragPan: !this.readOnly,
                doubleClickZoom: !this.readOnly,
                touchZoomRotate: !this.readOnly,
                zoom: this.zoomLevel || 0,
            };
            // Unfortunately, the map can't handle center set to null.
            if (this.centerLngLat) {
                options.center = this.centerLngLat;
            }
            const map = tt.map(options);

            /**
             * Mouse click on the map emits `selectedCoordinates` event only
             * when panoramas are shown.
             * Longitude is wrapped to the range (-180,180) by `wrap()`.
             */
            map.on('click', ({ lngLat, point: { x, y } }) => {
                if (this.isExtraLayerVisible) {
                    const { lng, lat } = lngLat.wrap();
                    if (this.drawingMode) {
                        if (this.drawingFinished) {
                            this.$emit('selectedCoordinates', { lng, lat, map });
                        } else {
                            this.$emit('addDrawingPoint', {
                                lng, lat, x, y,
                            });
                        }
                    } else {
                        this.$emit('selectedCoordinates', { lng, lat, map });
                    }
                }
            });

            /**
             * Events that change the center or bounds of displayed map
             * emit the new values.
             */
            map.on('dragend', this.emitMapState);
            map.on('resize', this.emitMapState);
            map.on('zoomend', this.emitMapState);

            map.on('drag', this.updateDrawingPoints);
            map.on('zoom', this.updateDrawingPoints);

            this.map = map;
            this.fit = map.resize;
        },

        /**
         * Map automatically pans & zooms to contain
         * its visible area within the specified geographical bounds.
         */
        fitMap() {
            const { map, bounds } = this;
            // No map - no action.
            if (!map) {
                return;
            }
            this.map.fitBounds(bounds);
        },

        /**
         * Zooms in by step of +1 to maximal value of `getMaxZoom()`.
         */
        zoomIn() {
            const { map } = this;
            // No map - no action.
            if (!map) {
                return;
            }

            const { getZoom, getMaxZoom } = map;
            map.easeTo({ zoom: Math.min(getZoom() + 1, getMaxZoom()) });
        },

        /**
         * Zooms out by step of -1 to minimal value of `getMinZoom()`.
         */
        zoomOut() {
            const { map } = this;
            // No map - no action.
            if (!map) {
                return;
            }

            const { getZoom, getMinZoom } = map;
            map.easeTo({ zoom: Math.max(getZoom() - 1, getMinZoom()) });
        },

        /**
         * Resets map orientation.
         */
        resetBearing() {
            const { map } = this;
            // No map - no action.
            if (!map) {
                return;
            }

            map.easeTo({ bearing: 0 });
        },

        /**
         * Resets map pitch (tilt).
         */
        resetPitch() {
            const { map } = this;
            // No map - no action.
            if (!map) {
                return;
            }

            map.easeTo({ pitch: 0 });
        },

        /**
         * Helper function for emitting the center and bounds
         * of currently displayed map.
         */
        emitMapState() {
            const { map, drawingLngLats, updateDrawingPoints } = this;

            if (!map) {
                return;
            }

            // $emit cannot be destructured
            this.$emit('center', map.getCenter());
            this.$emit('bounds', map.getBounds());

            updateDrawingPoints();
        },

        updateDrawingPoints() {
            const { map, drawingLngLats } = this;

            if (!map) {
                return;
            }

            const newPoints = drawingLngLats.map(map.project);
            this.$emit('newPoints', newPoints);
        }

    },

    mounted() {
        this.setupMap();
    },

    destroyed() {
        this.clearMap();
    },

    watch: {
        /**
         * Change of api key to other value than before triggers
         * map setup, its reinitialization.
         */
        apiKey(newValue, oldValue) {
            if (newValue === oldValue) {
                return;
            }

            this.setupMap();
        },

        /**
         * Updates markers (removes old ones and adds new ones)
         * on the map whenever search markers change, and on component's
         * initialization.
         */
        mapMarkers: {
            handler(newValue, oldValue = []) {
                this.$nextTick(() => {
                    oldValue.forEach(marker => marker.remove());
                    newValue.forEach(marker => marker.addTo(this.map));
                });
            },
            immediate: true,
        },

        /**
         * On this prop change, map automatically pans & zooms to contain
         * its visible area within the specified geographical bounds,
         * giving control of the map to parent component via props.
         */
        bounds(newValue) {
            if (newValue) {
                this.fitMap();
            }
        },

        /**
         * On any change of this computed var (which changes with
         * prop `this.center`), map pans new coords, therefore
         * giving control of the map to parent component via props.
         */
        centerLngLat(newValue) {
            if (newValue) {
                const { map, centerLngLat } = this;

                // No map - no action.
                if (!map) {
                    return;
                }

                map.flyTo({
                    center: centerLngLat,
                });
            }
        },

        /**
         * On any change of this prop, map zooms to new coords, therefore
         * giving control of the map to parent component via props.
         */
        zoomLevel(newValue) {
            if (newValue) {
                const { map, zoomLevel } = this;

                // No map - no action.
                if (!map) {
                    return;
                }

                map.flyTo({
                    zoom: zoomLevel,
                });
            }
        },

        arrow: {
            handler(position) {
                const { positionArrow, orientation } = this;

                if (!position) {
                    return;
                }

                const { position: { lng, lat } } = position;

                const element = createMarkerElement(ArrowMarker);
                const marker = new tt.Marker({
                    element,
                    anchor: 'center',
                }).setLngLat([lng, lat])
                    .setRotation(adjustOrientation(orientation));

                this.$nextTick(() => {
                    if (positionArrow) {
                        positionArrow.remove();
                    }
                    marker.addTo(this.map);
                });

                this.positionArrow = marker;
            },
            immediate: true,
        },

        orientation(newValue) {
            const { positionArrow } = this;
            if (!positionArrow) {
                return;
            }

            positionArrow.setRotation(adjustOrientation(newValue));
        },
    },
};
</script>
